import React, { FC, useState } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, SelectChangeEvent, Chip, TextField } from '@mui/material';
import { ETransactionType, TransactionTypeTranslations } from 'common/transactions.interface';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

interface IPaymentActionsProps {
  type: string;
  status: string;
  startDate: DateTime;
  endDate: DateTime;
  loading: boolean;
  accountType: string;
  onTypeChange: (value: string) => void;
  onStartDateChange: (value: DateTime) => void;
  onEndDateChange: (value: DateTime) => void;
  onStatusChange: (value: string) => void;
}

export const PaymentsActions: FC<IPaymentActionsProps> = ({
  type,
  status,
  startDate,
  endDate,
  loading,
  onTypeChange,
  onStartDateChange,
  onEndDateChange,
  onStatusChange,
}) => {
  const [selectedChip, setSelectedChip] = useState<string | null>(null);

  const handleTypeChange = (event: SelectChangeEvent) => {
    onTypeChange(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    onStatusChange(event.target.value);
  };

  const handleChipSelect = (range: '7d' | '30d' | 'month') => {
    if (range === '7d') {
      onStartDateChange(DateTime.now().minus({ days: 7 }));
      onEndDateChange(DateTime.now());
      setSelectedChip('7d');
    }

    if (range === '30d') {
      onStartDateChange(DateTime.now().minus({ days: 30 }));
      onEndDateChange(DateTime.now());
      setSelectedChip('30d');
    }

    if (range === 'month') {
      onStartDateChange(DateTime.now().startOf('month'));
      onEndDateChange(DateTime.now());
      setSelectedChip('month');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Chip
          color="primary"
          label="Últimos 7 dias"
          variant={selectedChip === '7d' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('7d')}
        />

        <Chip
          color="primary"
          label="Últimos 30 dias"
          variant={selectedChip === '30d' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('30d')}
        />

        <Chip
          color="primary"
          label="Mês atual"
          variant={selectedChip === 'month' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('month')}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }} mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Data inicial
          </Typography>

          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={startDate}
              onChange={(newValue) => {
                if (newValue) {
                  onStartDateChange(newValue);
                  setSelectedChip(null);
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={DateTime.now().minus({ days: 1 })}
            />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Data final
          </Typography>

          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={endDate}
              onChange={(newValue) => {
                if (newValue) {
                  onEndDateChange(newValue);
                  setSelectedChip(null);
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={DateTime.now()}
            />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Tipo
          </Typography>

          <Select
            value={type}
            onChange={handleTypeChange}
            size="small"
            sx={{ borderRadius: '8px', height: 37, fontSize: '14px' }}
            disabled={loading}
          >
            <MenuItem value="Todas">Todas</MenuItem>
            {[ETransactionType.MANUAL_PIX_OUT, ETransactionType.BANK_SLIP].map((transactionType) => (
              <MenuItem key={transactionType} value={transactionType}>
                {TransactionTypeTranslations[transactionType]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Status
          </Typography>

          <Select
            value={status}
            onChange={handleStatusChange}
            size="small"
            sx={{ borderRadius: '8px', height: 37, fontSize: '14px' }}
            disabled={loading}
          >
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="PENDENTE">Pendente</MenuItem>
            <MenuItem value="APROVADO">Aprovado</MenuItem>
            <MenuItem value="RECUSADO">Recusado</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </>
  );
};
